import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Paper, TextField, Tooltip, Typography, type ChipProps } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicIcon from '@mui/icons-material/Public';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DetailRow from '../../../shared/components/DetailRow';
import TicketContent from './TicketContent';
import { safeFormatDate } from '../../../shared';
import { useTicket, useAddTicketNote } from '../hooks/useTicket';
import { useEscalationLevel } from '../../customer/context/EscalationContext';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';

const STATUS_COLOR: Record<string, ChipProps['color']> = {
  open: 'info',
  pending: 'warning',
  resolved: 'success',
  closed: 'default',
};

const PRIORITY_COLOR: Record<string, ChipProps['color']> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
};

const fmt = (value: string | null | undefined) =>
  safeFormatDate(value, { dateStyle: 'medium', timeStyle: 'short' }, '—');

const noteTs = (value?: string): number => {
  const parsed = value ? Date.parse(value) : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

interface TicketDetailPanelProps {
  customerId: string | undefined;
  ticketId: string | null;
}

const TicketDetailPanel: React.FC<TicketDetailPanelProps> = ({ customerId, ticketId }) => {
  const { effectiveLevel } = useEscalationLevel();
  const canAddNote = effectiveLevel === 'l2' || effectiveLevel === 'admin';
  const { data: ticket = null, isLoading: loading, error: queryError } = useTicket(customerId, ticketId);
  const error = queryError ? extractErrorMessage(queryError, 'Failed to load ticket') : null;
  const addNoteMutation = useAddTicketNote();
  const submitting = addNoteMutation.isPending;
  const [noteBody, setNoteBody] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setNoteBody('');
    setSubmitError(null);
  }, [ticketId, customerId]);

  const handleAddNote = () => {
    if (!customerId || !ticketId || !noteBody.trim()) return;
    setSubmitError(null);
    addNoteMutation.mutate(
      { customerId, ticketId, body: noteBody.trim() },
      {
        onSuccess: () => setNoteBody(''),
        onError: (err) => setSubmitError(extractErrorMessage(err, 'Failed to add note')),
      },
    );
  };

  if (!ticketId) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a ticket to view its details
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  }

  if (!ticket) {
    return null;
  }

  const notesNewestFirst = [...ticket.notes].sort((a, b) => noteTs(b.created_at) - noteTs(a.created_at));

  return (
    <Box sx={{ p: 3, maxWidth: 820 }}>
      {/* Hero header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ flex: 1, wordBreak: 'break-word', lineHeight: 1.3 }}>
          {ticket.subject || `Ticket ${ticket.id}`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {ticket.status && (
            <Chip
              label={ticket.status}
              color={STATUS_COLOR[ticket.status] ?? 'default'}
              size="small"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          {ticket.priority && (
            <Chip
              label={ticket.priority}
              color={PRIORITY_COLOR[ticket.priority] ?? 'default'}
              size="small"
              variant="outlined"
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          {ticket.url && (
            <Tooltip title="Open ticket">
              <IconButton
                size="small"
                component="a"
                href={ticket.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open ticket"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        <Box component="span" sx={{ fontFamily: 'monospace' }}>#{ticket.id}</Box>
        {ticket.created_at && <> · opened {fmt(ticket.created_at)}</>}
        {ticket.updated_at && <> · updated {fmt(ticket.updated_at)}</>}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <DetailRow
          label="Requester"
          value={
            ticket.requester_name
              ? `${ticket.requester_name}${ticket.requester_email ? ` · ${ticket.requester_email}` : ''}`
              : ticket.requester_email || (ticket.requester_id != null ? `#${ticket.requester_id}` : undefined)
          }
        />
      </Box>

      {(ticket.description || ticket.description_html) && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            Description
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
            <TicketContent html={ticket.description_html} text={ticket.description} />
          </Paper>
        </Box>
      )}

      {ticket.resolution && (
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
            Resolution
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: 1.5, bgcolor: 'background.default', borderLeft: 3, borderLeftColor: 'success.main' }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
              {ticket.resolution}
            </Typography>
          </Paper>
        </Box>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Notes{ticket.notes.length > 0 ? ` (${ticket.notes.length})` : ''}
        </Typography>

        {canAddNote && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              size="small"
              placeholder="Add a private note…"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              disabled={submitting}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LockOutlinedIcon sx={{ fontSize: 14 }} /> Private — visible to agents, not the requester
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddNote}
                disabled={submitting || !noteBody.trim()}
                startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Add note
              </Button>
            </Box>
            {submitError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                {submitError}
              </Typography>
            )}
          </Box>
        )}

        {ticket.notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No notes.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {notesNewestFirst.map((note, idx) => (
              <Paper
                key={note.id ?? idx}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderLeft: 3,
                  borderLeftColor: note.private ? 'warning.main' : 'info.main',
                  bgcolor: note.private ? 'action.hover' : 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={note.private ? 'warning' : 'info'}
                    icon={note.private ? <LockOutlinedIcon /> : <PublicIcon />}
                    label={note.private ? 'Private' : 'Public'}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {fmt(note.created_at)}
                  </Typography>
                </Box>
                <TicketContent html={note.body_html} text={note.body} />
                {!note.body_html && !note.body && (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TicketDetailPanel;
